import * as d3 from 'd3';
import {
  convertCelsiusToFahrenheit,
  convertDeltaCelsiusToFahrenheit,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/conversions/valueConversions';
import BaseTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import VoltageChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/VoltageChannel/VoltageChannel';
import RaaExtremeFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import DeltaTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/DeltaTemperatureChannel/DeltaTemperatureChannel';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';
import D3Utility from './D3Utility';

const D3ChannelGraphingUtil = {
  TemperatureChannel: {
    getXAxis: (...args) => D3ChannelGraphingUtil.getXAxis(...args),
    drawXAxis: (...args) => D3ChannelGraphingUtil.drawXAxis(...args),
    // eslint-disable-next-line no-unused-vars
    getMarginLeft: (options = {}) => 50,
    getYAxis: (data, height, marginTop, marginBottom, options = { temperatureScale: 'C', thresholdValue1: 0, thresholdValue2: 30 }) => {
      const dataMin = d3.min(data, (d) => d.value);
      const dataMax = d3.max(data, (d) => d.value);
      let max = dataMax || 100;
      let min = dataMin < 0 ? dataMin : 0;

      if (options.thresholdValue1 !== null) {
        if (options.thresholdValue1 >= max) max = options.thresholdValue1;
        if (options.thresholdValue1 <= min) min = options.thresholdValue1 - 10;
      }

      if (options.thresholdValue2 !== null) {
        if (options.thresholdValue2 >= max) max = options.thresholdValue2;
        if (options.thresholdValue2 <= min) min = options.thresholdValue2 - 10;
      }
      min = options.temperatureScale === 'F' ? convertCelsiusToFahrenheit(min) : min;
      max = options.temperatureScale === 'F' ? convertCelsiusToFahrenheit(max) : max;
      return d3
        .scaleLinear()
        .domain([min, max + 10])
        .nice()
        .range([height - marginBottom, marginTop]);
    },
    drawYAxis: (svg, yAxis, marginLeft) => {
      svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(
          d3.axisLeft(yAxis).tickFormat((d) => `${d}`),
        )
        .selectAll('text')
        .style('font-size', '14px');
    },
    drawYAxisGrid: (svg, yAxis, width, marginLeft, marginRight) => {
      const grid = d3.axisLeft(yAxis)
        .tickSize(-(width - marginLeft - marginRight))
        .tickFormat('')
        .tickValues(yAxis.ticks());
      D3ChannelGraphingUtil.drawYAxis(svg, marginLeft, grid);
    },
    // eslint-disable-next-line no-unused-vars
    getDataLine: (xAxis, yAxis, options = { temperatureScale: 'C' }) => d3
      .line()
      .x((data) => xAxis(data.timestamp))
      .y((data) => yAxis(options.temperatureScale === 'F' ? convertCelsiusToFahrenheit(data.value) : data.value)),
    drawMouseoverPoints: (svg, data, xAxis, yAxis, popupDiv, marginLeft, marginTop, getDisplay, options = { temperatureScale: 'C' }) => {
      let popupTimeout;
      svg
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', (d) => xAxis(d.timestamp))
        .attr('cy', (d) => yAxis(options.temperatureScale === 'F' ? convertCelsiusToFahrenheit(d.value) : d.value))
        .attr('r', 5)
        .style('fill', '#EE3524')
        .style('opacity', '0')
        // eslint-disable-next-line func-names
        .on('mouseover', function (event, d) {
          if (popupTimeout) {
            clearTimeout(popupTimeout);
            popupTimeout = null;
          }
          d3.select(this).style('opacity', '1');
          const [mouseX, mouseY] = d3.pointer(event);
          const popup = getDisplay(d.timestamp, d.value);
          popupDiv.html(popup)
            .style('left', `${Math.max(mouseX - marginLeft + 10, 0)}px`)
            .style('white-space', 'nowrap')
            .style('padding', '5px 5px 5px 5px')
            .style('top', `${mouseY + marginTop - 10}px`)
            .style('opacity', 1);
        })
        // eslint-disable-next-line func-names
        .on('mouseout', function () {
          d3.select(this)
            .style('opacity', '0');
          popupTimeout = setTimeout(() => {
            popupDiv.transition()
              .duration(50)
              .style('opacity', 0);
            popupTimeout = null;
          }, 100);
        });
    },

  },
  BooleanChannel: {
    getXAxis: (...args) => D3ChannelGraphingUtil.getXAxis(...args),
    drawXAxis: (...args) => D3ChannelGraphingUtil.drawXAxis(...args),
    getMarginLeft: () => 76,
    getYAxis: (data, height, marginTop, marginBottom, options = {
      falseDisplayValue: 'False',
      trueDisplayValue: 'True',
    }) => d3
      .scaleBand()
      .domain([options.falseDisplayValue, options.trueDisplayValue])
      .range([height - marginBottom, marginTop]),
    drawYAxis: (svg, yAxis, marginLeft) => {
      svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(d3.axisLeft(yAxis))
        .selectAll('text')
        .style('font-size', '14px')
        .style('text-anchor', 'end')
        // eslint-disable-next-line func-names
        .each(function () {
          const text = d3.select(this);
          const words = text.text().split(/\s+/);
          let word;
          const lineHeight = 1.2; // in em
          let lineNumber = 0;
          text.text(null);
          // eslint-disable-next-line no-cond-assign
          while (word = words.shift()) {
            text
              .append('tspan')
              .attr('x', -9) // Move to the left
              .attr('dy', `${lineNumber > 0 ? lineHeight : -lineHeight * (words.length / 2) + 0.3}em`)
              .text(word);
            lineNumber += 1;
          }
        });
    },
    drawYAxisGrid: (svg, yAxis, width, marginLeft, marginRight) => {
      const grid = d3.axisLeft(yAxis)
        .tickSize(-(width - marginLeft - marginRight))
        .tickFormat('')
        .ticks(2);
      D3ChannelGraphingUtil.drawYAxis(svg, marginLeft, grid);
    },
    getDataLine: (xAxis, yAxis, options = {
      trueDisplayValue: 'True',
      falseDisplayValue: 'False',
    }) => d3
      .line()
      .x((data) => xAxis(data.timestamp))
      .y((data) => yAxis(data.value >= 1
        ? options.trueDisplayValue : options.falseDisplayValue) + yAxis.bandwidth() / 2)
      .curve(d3.curveStepAfter),
    drawMouseoverPoints: (svg, data, xAxis, yAxis,
      popupDiv, marginLeft, marginTop, getDisplay, options = {
        trueDisplayValue: 'True',
        falseDisplayValue: 'False',
      }) => {
      let popupTimeout;
      svg
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', (d) => xAxis(d.timestamp))
        .attr('cy', (d) => yAxis(d.value >= 1 ? options.trueDisplayValue : options.falseDisplayValue) + yAxis.bandwidth() / 2)
        .attr('r', 5)
        .style('opacity', '0')
        .style('fill', '#EE3524')
        // eslint-disable-next-line func-names
        .on('mouseover', function (event, d) {
          if (popupTimeout) {
            clearTimeout(popupTimeout);
            popupTimeout = null;
          }
          d3.select(this)
            .style('opacity', '1');
          const [mouseX, mouseY] = d3.pointer(event);
          popupDiv.style('pointer-events', 'none');
          popupDiv.html(`${getDisplay(d.timestamp, d.value)}`)
            .style('left', `${mouseX - marginLeft + 10}px`)
            .style('white-space', 'nowrap')
            .style('padding', '5px 5px 5px 5px')
            .style('top', `${mouseY + marginTop - 10}px`)
            .style('opacity', 1);
        })
        // eslint-disable-next-line func-names
        .on('mouseout', function () {
          d3.select(this)
            .transition()
            .duration(50)
            .style('opacity', '0');
          popupTimeout = setTimeout(() => {
            popupDiv.transition()
              .duration(50)
              .style('opacity', 0);
            popupTimeout = null;
          }, 100);
        });
    },
  },
  VoltageChannel: {
    getXAxis: (...args) => D3ChannelGraphingUtil.getXAxis(...args),
    drawXAxis: (...args) => D3ChannelGraphingUtil.drawXAxis(...args),
    // eslint-disable-next-line no-unused-vars
    getMarginLeft: (options = {}) => 50,
    // eslint-disable-next-line no-unused-vars
    getYAxis: (data, height, marginTop, marginBottom, options = {}) => d3
      .scaleLinear()
      .domain([-1, 7])
      .nice()
      .range([height - marginBottom, marginTop]),
    drawYAxis: (svg, yAxis, marginLeft) => {
      svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(d3.axisLeft(yAxis).tickFormat(d3.format('.2f')))
        .selectAll('text')
        .style('font-size', '14px');
    },
    drawYAxisGrid: (svg, yAxis, width, marginLeft, marginRight) => {
      const grid = d3.axisLeft(yAxis)
        .tickSize(-(width - marginLeft - marginRight))
        .tickFormat('')
        .tickValues(yAxis.ticks());
      D3ChannelGraphingUtil.drawYAxis(svg, marginLeft, grid);
    },
    // eslint-disable-next-line no-unused-vars
    getDataLine: (xAxis, yAxis, options = { }) => d3
      .line()
      .x((data) => xAxis(data.timestamp))
      .y((data) => yAxis(data.value)),
    drawMouseoverPoints: (svg, data, xAxis, yAxis,
      // eslint-disable-next-line no-unused-vars
      popupDiv, marginLeft, marginTop, getDisplay, options = {}) => {
      let popupTimeout;
      svg
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', (d) => xAxis(d.timestamp))
        .attr('cy', (d) => yAxis(d.value))
        .attr('r', 5)
        .style('fill', '#EE3524')
        .style('opacity', '0')
        // eslint-disable-next-line func-names
        .on('mouseover', function (event, d) {
          if (popupTimeout) {
            clearTimeout(popupTimeout);
            popupTimeout = null;
          }
          d3.select(this).style('opacity', '1');
          const [mouseX, mouseY] = d3.pointer(event);
          popupDiv.html(`${getDisplay(d.timestamp, d.value)}`)
            .style('left', `${Math.max(mouseX - marginLeft + 10, 0)}px`)
            .style('white-space', 'nowrap')
            .style('padding', '5px 5px 5px 5px')
            .style('top', `${mouseY + marginTop - 10}px`)
            .style('opacity', 1);
        })
        // eslint-disable-next-line func-names
        .on('mouseout', function () {
          d3.select(this)
            .transition()
            .duration(50)
            .style('opacity', '0');
          popupTimeout = setTimeout(() => {
            popupDiv.transition()
              .duration(50)
              .style('opacity', 0);
            popupTimeout = null;
          }, 100);
        });
    },
  },

  drawYAxis: (svg, marginLeft, grid) => {
    svg.append('g')
      .attr('class', 'y axis-grid')
      .attr('transform', `translate(${marginLeft}, 0)`)
      .style('opacity', '0.3')
      .call(grid);
  },
  getXAxis: (startTime, endTime, width, marginLeft, marginRight) => d3
    .scaleTime()
    .domain([startTime, endTime])
    .range([marginLeft, width - marginRight]),
  drawXAxis: (svg, xAxis, height, marginTop, marginBottom, timeZone) => {
    const numberOfTicks = 3;
    const tickValues = d3.range(xAxis.domain()[0],
      xAxis.domain()[1],
      (xAxis.domain()[1] - xAxis.domain()[0]) / numberOfTicks);
    svg
      .append('g')
      .style('font-size', '14px')
      .attr('transform', `translate(0,${height - marginBottom})`)
      .call(d3.axisBottom(xAxis)
        .tickValues(tickValues)
        .tickSizeOuter(0)
        .tickFormat((d) => D3Utility.getTimeZoneDate(timeZone, d)));
    svg.select('.tick text').style('opacity', 0);
    const xAxisGrid = d3.axisBottom(xAxis)
      .tickSize(-(height - marginBottom - marginTop))
      .tickFormat('')
      .tickValues(tickValues);
    svg.append('g')
      .attr('class', 'x-axis-grid')
      .attr('transform', `translate(0, ${height - marginBottom})`)
      .style('opacity', '.3')
      .style('font-size', '14px')
      .call(xAxisGrid);
  },
  DeltaTemperatureChannel: {
    getXAxis: (...args) => D3ChannelGraphingUtil.getXAxis(...args),
    drawXAxis: (...args) => D3ChannelGraphingUtil.drawXAxis(...args),
    // eslint-disable-next-line no-unused-vars
    getMarginLeft: (options = {}) => 50,
    getYAxis: (data, height, marginTop, marginBottom, options = { temperatureScale: 'C', thresholdValue1: 0, thresholdValue2: 30 }) => {
      const dataMin = d3.min(data, (d) => d.value);
      const dataMax = d3.max(data, (d) => d.value);
      let max = dataMax || 100;
      let min = dataMin < 0 ? dataMin : 0;

      if (options.thresholdValue1 !== null) {
        if (options.thresholdValue1 >= max) max = options.thresholdValue1;
        if (options.thresholdValue1 <= min) min = options.thresholdValue1 - 10;
      }

      if (options.thresholdValue2 !== null) {
        if (options.thresholdValue2 >= max) max = options.thresholdValue2;
        if (options.thresholdValue2 <= min) min = options.thresholdValue2 - 10;
      }
      min = options.temperatureScale === 'F' ? convertDeltaCelsiusToFahrenheit(min) : min;
      max = options.temperatureScale === 'F' ? convertDeltaCelsiusToFahrenheit(max) : max;
      return d3
        .scaleLinear()
        .domain([min, max + 10])
        .nice()
        .range([height - marginBottom, marginTop]);
    },
    drawYAxis: (svg, yAxis, marginLeft) => {
      svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(d3.axisLeft()
          .scale(yAxis))
        .selectAll('text')
        .style('font-size', '14px');
    },
    drawYAxisGrid: (svg, yAxis, width, marginLeft, marginRight) => {
      const grid = d3.axisLeft(yAxis)
        .tickSize(-(width - marginLeft - marginRight))
        .tickFormat('')
        .tickValues(yAxis.ticks());
      D3ChannelGraphingUtil.drawYAxis(svg, marginLeft, grid);
    },
    // eslint-disable-next-line no-unused-vars
    getDataLine: (xAxis, yAxis, options = { temperatureScale: 'C' }) => d3
      .line()
      .x((data) => xAxis(data.timestamp))
      .y((data) => yAxis(options.temperatureScale === 'F' ? convertDeltaCelsiusToFahrenheit(data.value) : data.value)),
    drawMouseoverPoints: (svg, data, xAxis, yAxis, popupDiv, marginLeft, marginTop, getDisplay, options = { temperatureScale: 'C' }) => {
      let popupTimeout;
      svg
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', (d) => xAxis(d.timestamp))
        .attr('cy', (d) => yAxis(options.temperatureScale === 'F' ? convertDeltaCelsiusToFahrenheit(d.value) : d.value))
        .attr('r', 5)
        .style('fill', '#EE3524')
        .style('opacity', '0')
        // eslint-disable-next-line func-names
        .on('mouseover', function (event, d) {
          if (popupTimeout) {
            clearTimeout(popupTimeout);
            popupTimeout = null;
          }
          d3.select(this).style('opacity', '1');
          const [mouseX, mouseY] = d3.pointer(event);
          const popup = getDisplay(d.timestamp, d.value);
          popupDiv.html(popup)
            .style('left', `${Math.max(mouseX - marginLeft + 10, 0)}px`)
            .style('white-space', 'nowrap')
            .style('padding', '5px 5px 5px 5px')
            .style('top', `${mouseY + marginTop - 10}px`)
            .style('opacity', 1);
        })
        // eslint-disable-next-line func-names
        .on('mouseout', function () {
          d3.select(this)
            .style('opacity', '0');
          popupTimeout = setTimeout(() => {
            popupDiv.transition()
              .duration(50)
              .style('opacity', 0);
            popupTimeout = null;
          }, 100);
        });
    },

  },
  DefaultChannel: {
    getXAxis: (...args) => D3ChannelGraphingUtil.getXAxis(...args),
    drawXAxis: (...args) => D3ChannelGraphingUtil.drawXAxis(...args),
    // eslint-disable-next-line no-unused-vars
    getMarginLeft: (options = {}) => 50,
    getYAxis: (data, height, marginTop, marginBottom,
      options = { thresholdValue1: 0, thresholdValue2: 30 }) => {
      const dataMin = d3.min(data, (d) => d.value);
      const dataMax = d3.max(data, (d) => d.value);
      let max = dataMax || 100;
      let min = dataMin < 0 ? dataMin : 0;

      if (options.thresholdValue1 !== null) {
        if (options.thresholdValue1 >= max) max = options.thresholdValue1;
        if (options.thresholdValue1 <= min) min = options.thresholdValue1 - 10;
      }

      if (options.thresholdValue2 !== null) {
        if (options.thresholdValue2 >= max) max = options.thresholdValue2;
        if (options.thresholdValue2 <= min) min = options.thresholdValue2 - 10;
      }

      return d3
        .scaleLinear()
        .domain([min, max + 10])
        .nice()
        .range([height - marginBottom, marginTop]);
    },
    drawYAxis: (svg, yAxis, marginLeft) => {
      svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(d3.axisLeft()
          .scale(yAxis))
        .selectAll('text')
        .style('font-size', '14px');
    },
    drawYAxisGrid: (svg, yAxis, width, marginLeft, marginRight) => {
      const grid = d3.axisLeft(yAxis)
        .tickSize(-(width - marginLeft - marginRight))
        .tickFormat('')
        .tickValues(yAxis.ticks());
      D3ChannelGraphingUtil.drawYAxis(svg, marginLeft, grid);
    },
    // eslint-disable-next-line no-unused-vars
    getDataLine: (xAxis, yAxis, options = { temperatureScale: 'C' }) => d3
      .line()
      .x((data) => xAxis(data.timestamp))
      .y((data) => yAxis(data.value)),
    drawMouseoverPoints: (svg,
      // eslint-disable-next-line no-unused-vars
      data, xAxis, yAxis, popupDiv, marginLeft, marginTop, getDisplay, options = { }) => {
      let popupTimeout;
      svg
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', (d) => xAxis(d.timestamp))
        .attr('cy', (d) => yAxis(d.value))
        .attr('r', 5)
        .style('fill', '#EE3524')
        .style('opacity', '0')
        // eslint-disable-next-line func-names
        .on('mouseover', function (event, d) {
          if (popupTimeout) {
            clearTimeout(popupTimeout);
            popupTimeout = null;
          }
          d3.select(this).style('opacity', '1');
          const [mouseX, mouseY] = d3.pointer(event);
          const popup = getDisplay(d.timestamp, d.value);
          popupDiv.html(popup)
            .style('left', `${Math.max(mouseX - marginLeft + 10, 0)}px`)
            .style('white-space', 'nowrap')
            .style('padding', '5px 5px 5px 5px')
            .style('top', `${mouseY + marginTop - 10}px`)
            .style('opacity', 1);
        })
        // eslint-disable-next-line func-names
        .on('mouseout', function () {
          d3.select(this)
            .style('opacity', '0');
          popupTimeout = setTimeout(() => {
            popupDiv.transition()
              .duration(50)
              .style('opacity', 0);
            popupTimeout = null;
          }, 100);
        });
    },

  },
  getUtilitySetByChannel: (channelClass) => {
    const classToUtilityMap = new Map();
    classToUtilityMap.set(BaseTemperatureChannel, D3ChannelGraphingUtil.TemperatureChannel);
    classToUtilityMap.set(BooleanChannel, D3ChannelGraphingUtil.BooleanChannel);
    classToUtilityMap.set(RaaExtremeFaultChannel, D3ChannelGraphingUtil.BooleanChannel);
    classToUtilityMap.set(VoltageChannel, D3ChannelGraphingUtil.VoltageChannel);
    classToUtilityMap.set(DeltaTemperatureChannel, D3ChannelGraphingUtil.DeltaTemperatureChannel);
    classToUtilityMap.set(IntegerAsBooleanChannel, D3ChannelGraphingUtil.BooleanChannel);
    let prototype = Object.getPrototypeOf(channelClass);
    while (prototype !== Object.prototype) {
      const utilities = classToUtilityMap.get(prototype.constructor);
      if (utilities) {
        return utilities;
      }
      prototype = Object.getPrototypeOf(prototype);
    }
    return D3ChannelGraphingUtil.DefaultChannel;
  },
};

export default D3ChannelGraphingUtil;
